import Component from '@glimmer/component';
import type Owner from '@ember/owner';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import FormInput from 'scaleflow/components/atoms/forms/input';
import Loader from 'scaleflow/components/loader';
import ExclamationCircle from 'scaleflow/components/tailwind-icons/exclamation-circle';
import Squares2x2 from 'scaleflow/components/tailwind-icons/squares-2x2';
import CodeXml from 'scaleflow/components/tailwind-icons/code-xml';
import CheckCircle from 'scaleflow/components/tailwind-icons/check-circle';
import Tippy from 'ember-tippy/components/tippy';
import AtomsTooltip from 'scaleflow/components/atoms/tooltip';
import onUpdate from 'scaleflow/modifiers/render-modifiers/on-update';

export interface GitlabPathProject {
  id: number;
  path: string;
}

export interface GitlabPathResult {
  path: string;
  kind: 'group' | 'project';
  projects?: GitlabPathProject[];
  error?: string;
}

export interface GitlabPathListing {
  results: GitlabPathResult[];
  /** Subgroups with projects that were not named in the input. */
  discoveredPaths: string[];
}

export interface ConnectorsGitlabConnectFormSignature {
  Element: HTMLFormElement;
  Args: {
    Named: {
      /** Submit-button copy — comes from `connectors-config.ts`'s `connectLabel`. */
      connectLabel?: string;
      /** Already-translated message for the last failed attempt. */
      error?: string | null;
      /**
       * Resolves each comma-separated path against the instance: a group
       * path lists its projects, a project path resolves to itself. Neither
       * `/groups` nor `/projects?membership=true` can be trusted to enumerate
       * a group-scoped token's own access — see the blueprint — so the user
       * names what to check rather than us discovering it.
       */
      onListPaths: (
        instanceUrl: string,
        token: string,
        paths: string[],
      ) => Promise<GitlabPathListing>;
      onConnect: (
        instanceUrl: string,
        token: string,
        paths: string[],
      ) => Promise<void> | void;
      /**
       * Pre-fills the form from an existing integration record. The token is
       * never included — it is write-only from the moment it's sealed, never
       * readable back off any record.
       */
      instanceUrl?: string;
      groupPaths?: string[];
      /**
       * True once a connection already exists. Locks every field and hides
       * the List/Save actions — there is deliberately no edit-in-place flow,
       * so this is a read-only view of what's connected, not a form.
       */
      disabled?: boolean;
    };
  };
}

/**
 * Connects GitLab with a pasted instance URL, token, and the groups/projects
 * to scan.
 *
 * Two steps, not one: **List** resolves the paths against the instance so the
 * user can see exactly what will be scanned before anything is saved; **Save**
 * is the only step that seals the token and persists the integration. Listing
 * never talks to Scaleflow's API — it is the browser calling the user's own
 * GitLab instance directly, the same as the preflight check.
 *
 * The token lives only in this component's own tracked state and leaves it
 * only as an argument to `@onListPaths`/`@onConnect`. It is never logged and
 * never echoed back into the error region.
 */
export default class ConnectorsGitlabConnectForm extends Component<ConnectorsGitlabConnectFormSignature> {
  @tracked instanceUrl: string;
  @tracked token = '';
  @tracked pathsInput: string;
  @tracked phase: 'form' | 'listing' | 'preview' | 'saving' = 'form';
  @tracked results: GitlabPathResult[] = [];
  @tracked expanded = new Set<string>();
  @tracked listAttempts = 0;

  /**
   * Caps the append-and-relist loop that folds in subgroups-with-projects.
   * Each pass can only discover paths one level deeper than the last, so this
   * bounds worst-case browser-to-GitLab traffic against a deeply nested group
   * tree rather than looping until the whole hierarchy is exhausted.
   */
  maxListAttempts = 6;

  constructor(
    owner: Owner,
    args: ConnectorsGitlabConnectFormSignature['Args']['Named'],
  ) {
    super(owner, args);
    // Never the token — it's write-only from the moment it's sealed and is
    // never readable back off any record, disabled view or not.
    this.instanceUrl = args.instanceUrl ?? '';
    this.pathsInput = args.groupPaths?.join(', ') ?? '';
  }

  /**
   * A Glimmer component's constructor runs once, on first creation — it does
   * not re-run when `@args` change. This form is never torn down and
   * recreated across connect/disconnect (it's the same tag on the page the
   * whole time, only `@disabled` toggles), so without this, disconnecting
   * left the just-vacated `instanceUrl`/`pathsInput` sitting in local state
   * instead of the form clearing for a fresh connection.
   *
   * Fired by the `onUpdate` modifier below, which only calls back on an
   * actual change to `@disabled` — never on first render, where the
   * constructor's own initialization already applies. Only resets on the
   * true → false transition (unlocking): the other direction (locking, right
   * after a successful connect) already shows the values the user just
   * submitted, so there's nothing to reset there.
   */
  @action
  syncFromArgsOnUnlock(_element: Element, disabled: unknown): void {
    if (disabled) return;

    this.instanceUrl = this.args.instanceUrl ?? '';
    this.pathsInput = this.args.groupPaths?.join(', ') ?? '';
    this.token = '';
    this.phase = 'form';
    this.results = [];
    this.expanded = new Set();
    this.listAttempts = 0;
  }

  get isReadOnly(): boolean {
    return !!this.args.disabled;
  }

  get fieldsDisabled(): boolean {
    return this.isPreview || this.isReadOnly;
  }

  get paths(): string[] {
    return this.pathsInput
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }

  get canList(): boolean {
    return (
      this.phase === 'form' &&
      this.instanceUrl.trim().length > 0 &&
      this.token.trim().length > 0 &&
      this.paths.length > 0
    );
  }

  get isListing(): boolean {
    return this.phase === 'listing';
  }

  get isPreview(): boolean {
    return this.phase === 'preview' || this.phase === 'saving';
  }

  get isSaving(): boolean {
    return this.phase === 'saving';
  }

  get listDisabled(): boolean {
    return !this.canList;
  }

  @action
  updateInstanceUrl(event: Event) {
    this.instanceUrl = (event.target as HTMLInputElement).value;
  }

  @action
  updateToken(event: Event) {
    this.token = (event.target as HTMLInputElement).value;
  }

  @action
  updatePaths(event: Event) {
    this.pathsInput = (event.target as HTMLInputElement).value;
  }

  @action
  isExpanded(path: string): boolean {
    return this.expanded.has(path);
  }

  @action
  toggleExpanded(path: string) {
    const next = new Set(this.expanded);
    if (next.has(path)) {
      next.delete(path);
    } else {
      next.add(path);
    }
    this.expanded = next;
  }

  @action
  async submit(event: Event) {
    event.preventDefault();
    if (!this.canList) return;

    this.listAttempts = 0;
    await this.listAndFoldInSubgroups();
  }

  /**
   * Lists the current paths, then — if any listed group turned up a subgroup
   * with projects that was not named — appends it to the paths field and
   * lists again. Append-only, deduplicated by `paths` itself: the field is
   * the single source of truth for what gets checked, so there is no separate
   * "subgroup" render state, only the same preview seeing a wider input each
   * pass. Recurses naturally to arbitrary depth this way; `maxListAttempts`
   * is the only thing stopping it.
   */
  private async listAndFoldInSubgroups() {
    this.phase = 'listing';
    try {
      // Trimmed because a pasted URL or token routinely picks up a trailing
      // newline, which the instance would reject as malformed.
      const { results, discoveredPaths } = await this.args.onListPaths(
        this.instanceUrl.trim(),
        this.token.trim(),
        this.paths,
      );

      if (
        discoveredPaths.length > 0 &&
        this.listAttempts < this.maxListAttempts
      ) {
        this.listAttempts++;
        const merged = new Set([...this.paths, ...discoveredPaths]);
        this.pathsInput = [...merged].join(', ');
        await this.listAndFoldInSubgroups();
        return;
      }

      this.results = results;
      this.phase = 'preview';
      // Groups default to expanded so the preview is useful without a click.
      this.expanded = new Set(
        this.results.filter((r) => r.kind === 'group').map((r) => r.path),
      );
    } catch {
      // As with `save`, deliberately not logged — a log line here is exactly
      // where a pasted credential would leak.
      this.phase = 'form';
    }
  }

  @action
  backToForm() {
    this.phase = 'form';
    this.results = [];
  }

  @action
  async save() {
    if (this.phase !== 'preview') return;
    this.phase = 'saving';
    try {
      await this.args.onConnect(
        this.instanceUrl.trim(),
        this.token.trim(),
        this.paths,
      );
    } catch {
      // Swallowed, and deliberately not logged. `@onConnect` owns turning a
      // failure into the message rendered in `@error`; a log line here is
      // exactly where a pasted credential would leak.
    } finally {
      this.phase = 'preview';
    }
  }

  <template>
    <form
      class="mb-10 max-w-lg space-y-6"
      data-test-gitlab-connect-form
      {{on "submit" this.submit}}
      {{onUpdate this.syncFromArgsOnUnlock @disabled}}
      ...attributes
    >
      <div class="space-y-1">
        <FormInput
          @id="gitlab-instance-url"
          @label={{t "connectors.gitlab.instance-url-label"}}
          @placeholder={{t "connectors.gitlab.instance-url-placeholder"}}
          @value={{this.instanceUrl}}
          @disabled={{this.fieldsDisabled}}
          @containerClass="w-full"
          class="disabled:!text-gray-600"
          data-test-gitlab-instance-url
          {{on "input" this.updateInstanceUrl}}
        />
        <p class="pl-2 text-xs font-normal text-gray-600">
          {{t "connectors.gitlab.instance-url-help"}}
        </p>
      </div>

      <div class="space-y-1">
        <FormInput
          @id="gitlab-token"
          @type="password"
          @label={{t "connectors.gitlab.token-label"}}
          @placeholder={{if
            this.isReadOnly
            (t "connectors.gitlab.token-hidden-placeholder")
            (t "connectors.gitlab.token-placeholder")
          }}
          @value={{this.token}}
          @disabled={{this.fieldsDisabled}}
          @containerClass="w-full"
          class="disabled:!text-gray-600"
          autocomplete="off"
          data-test-gitlab-token
          {{on "input" this.updateToken}}
        />

        <p class="pl-2 text-xs font-normal text-gray-600">
          {{t "connectors.gitlab.token-help"}}
          {{#unless this.fieldsDisabled}}
            <span
              class="cursor-help underline decoration-dotted underline-offset-2"
              data-test-gitlab-permissions-trigger
            >
              {{t "connectors.gitlab.permissions-hover-trigger"}}
              <Tippy @placement="bottom-start" @trigger="mouseenter focus">
                <AtomsTooltip
                  class="!ml-0 max-w-xs !text-left"
                  data-test-gitlab-permissions-checklist
                >
                  <p class="font-medium text-white">
                    {{t "connectors.gitlab.permissions-title"}}
                  </p>

                  <p class="mt-2 font-medium text-white">
                    {{t "connectors.gitlab.permissions-user-heading"}}
                  </p>
                  <p class="mt-1 ml-3 font-medium text-white">
                    {{t
                      "connectors.gitlab.permissions-user-system-access-heading"
                    }}
                  </p>
                  <div class="mt-1 ml-6 flex items-start gap-1.5">
                    <CheckCircle class="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      <span class="font-medium">{{t
                          "connectors.gitlab.permissions-user-pat-read"
                        }}</span>
                      —
                      {{t "connectors.gitlab.permissions-user-pat-read-reason"}}
                    </span>
                  </div>

                  <p class="mt-2 font-medium text-white">
                    {{t "connectors.gitlab.permissions-group-heading"}}
                  </p>
                  <p class="mt-1 ml-3 font-medium text-white">
                    {{t "connectors.gitlab.permissions-group-groups-heading"}}
                  </p>
                  <div class="mt-1 ml-6 flex items-start gap-1.5">
                    <CheckCircle class="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      <span class="font-medium">{{t
                          "connectors.gitlab.permissions-group-read"
                        }}</span>
                      —
                      {{t "connectors.gitlab.permissions-group-read-reason"}}
                    </span>
                  </div>
                  <p class="mt-2 ml-3 font-medium text-white">
                    {{t "connectors.gitlab.permissions-group-projects-heading"}}
                  </p>
                  <div class="mt-1 ml-6 flex items-start gap-1.5">
                    <CheckCircle class="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      <span class="font-medium">{{t
                          "connectors.gitlab.permissions-project-read"
                        }}</span>
                      —
                      {{t "connectors.gitlab.permissions-project-read-reason"}}
                    </span>
                  </div>
                  <p class="mt-2 ml-3 font-medium text-white">
                    {{t
                      "connectors.gitlab.permissions-group-repository-heading"
                    }}
                  </p>
                  <div class="mt-1 ml-6 flex items-start gap-1.5">
                    <CheckCircle class="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      <span class="font-medium">{{t
                          "connectors.gitlab.permissions-code-download"
                        }}</span>
                      —
                      {{t "connectors.gitlab.permissions-code-download-reason"}}
                    </span>
                  </div>
                </AtomsTooltip>
              </Tippy>
            </span>
          {{/unless}}
        </p>
      </div>

      <div class="space-y-1">
        <FormInput
          @id="gitlab-paths"
          @label={{t "connectors.gitlab.paths-label"}}
          @placeholder={{t "connectors.gitlab.paths-placeholder"}}
          @value={{this.pathsInput}}
          @disabled={{this.fieldsDisabled}}
          @containerClass="w-full"
          class="disabled:!text-gray-600"
          data-test-gitlab-paths
          {{on "input" this.updatePaths}}
        />
        <p class="pl-2 text-xs font-normal text-gray-600">
          {{t "connectors.gitlab.paths-help"}}
        </p>
      </div>

      {{#if @error}}
        <div
          class="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700"
          role="alert"
          data-test-gitlab-connect-error
        >
          <ExclamationCircle class="w-4 h-4 shrink-0 mt-0.5" />
          <span>{{@error}}</span>
        </div>
      {{/if}}

      {{#if this.isPreview}}
        <div class="rounded-md border divide-y" data-test-gitlab-paths-preview>
          {{#each this.results as |result|}}
            <div class="p-3">
              {{#if (eq result.kind "group")}}
                <button
                  type="button"
                  class="flex items-center gap-2 w-full text-left font-medium"
                  data-test-gitlab-path-toggle={{result.path}}
                  {{on "click" (fn this.toggleExpanded result.path)}}
                >
                  <span>{{if (this.isExpanded result.path) "▾" "▸"}}</span>
                  <Squares2x2 class="w-4 h-4 shrink-0 text-gray-400" />
                  <span>{{result.path}}</span>
                  {{#unless result.error}}
                    <span
                      class="text-gray-400 text-sm"
                    >({{result.projects.length}})</span>
                  {{/unless}}
                </button>
              {{else}}
                <div
                  class="flex items-center gap-2 font-medium"
                  data-test-gitlab-path-leaf={{result.path}}
                >
                  <CodeXml class="w-4 h-4 shrink-0 text-gray-400" />
                  <span>{{result.path}}</span>
                </div>
              {{/if}}

              {{#if result.error}}
                <p
                  class="text-sm text-red-600 mt-1"
                  data-test-gitlab-path-error={{result.path}}
                >
                  {{result.error}}
                </p>
              {{else if (this.isExpanded result.path)}}
                {{#if result.projects.length}}
                  <ul class="mt-2 ml-6 space-y-1 text-sm text-gray-600">
                    {{#each result.projects as |project|}}
                      <li
                        class="flex items-center gap-2"
                        data-test-gitlab-preview-project={{project.path}}
                      >
                        <CodeXml class="w-3.5 h-3.5 shrink-0 text-gray-400" />
                        <span>{{project.path}}</span>
                      </li>
                    {{/each}}
                  </ul>
                {{else}}
                  <p
                    class="mt-2 ml-6 text-sm text-gray-400 italic"
                    data-test-gitlab-path-empty={{result.path}}
                  >
                    {{t "connectors.gitlab.no-projects-found"}}
                  </p>
                {{/if}}
              {{/if}}
            </div>
          {{/each}}
        </div>
      {{/if}}

      {{#unless this.isReadOnly}}
        <div class="flex gap-3">
          {{#if this.isPreview}}
            <button
              type="button"
              disabled={{this.isSaving}}
              class="inline-flex items-center gap-2 rounded-md border px-4 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
              data-test-gitlab-back
              {{on "click" this.backToForm}}
            >
              {{t "connectors.gitlab.back"}}
            </button>
            <button
              type="button"
              disabled={{this.isSaving}}
              class="inline-flex items-center gap-2 rounded-md bg-indigo-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-60 disabled:cursor-not-allowed"
              data-test-gitlab-connect-submit
              {{on "click" this.save}}
            >
              {{#if this.isSaving}}
                <Loader
                  class="!w-4 !h-4 !border-2 !border-t-2 border-white/30 border-t-white"
                />
                {{t "connectors.gitlab.submit-connecting"}}
              {{else}}
                {{@connectLabel}}
              {{/if}}
            </button>
          {{else}}
            <button
              type="submit"
              disabled={{this.listDisabled}}
              class="inline-flex items-center gap-2 rounded-md bg-indigo-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-60 disabled:cursor-not-allowed"
              data-test-gitlab-list-submit
            >
              {{#if this.isListing}}
                <Loader
                  class="!w-4 !h-4 !border-2 !border-t-2 border-white/30 border-t-white"
                />
                {{t "connectors.gitlab.submit-listing"}}
              {{else}}
                {{t "connectors.gitlab.submit-list"}}
              {{/if}}
            </button>
          {{/if}}
        </div>
      {{/unless}}
    </form>
  </template>
}
